(() => {
  const header = document.querySelector("header.navimow-header");
  const nav = header?.querySelector(".navimow-header_nav");
  const menu = nav?.querySelector(".navimow-header_menu");
  const mobileMenuInner = nav?.querySelector(".navimow-header_mobile-menu-inner");
  const mobileMenuButton = header?.querySelector(
    ".navimow-header_mobile-menu-button",
  );
  if (!header || !nav || !menu || !mobileMenuInner || !mobileMenuButton) return;

  const mediaList = window.matchMedia("(min-width: 1024px)");
  const MOBILE_MENU_TRANSITION_MS = 240;
  const addMediaChangeListener = (query, callback) => {
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", callback);
      return;
    }
    query.addListener(callback);
  };

  const isMobileMenuOpen = () =>
    mobileMenuButton.getAttribute("aria-expanded") === "true";

  const updateNavAccessibility = () => {
    const isInteractive = mediaList.matches || isMobileMenuOpen();
    nav.setAttribute("aria-hidden", isInteractive ? "false" : "true");
  };

  const navScrollButtons = nav.querySelectorAll(".navimow-header_nav-arrow");
  let navOverflowFrame = 0;

  const syncDesktopMenuPanelPosition = (button) => {
    const headerRect = header.getBoundingClientRect();
    header.style.setProperty(
      "--navimow-header-menu-panel-top",
      `${Math.round(headerRect.bottom)}px`,
    );

    if (!button) return;

    const listDetail = button.closest(".header-menu-list");
    if (!listDetail) return;

    const buttonRect = button.getBoundingClientRect();
    listDetail.style.setProperty(
      "--header-menu-list-content-left",
      `${Math.round(buttonRect.left + buttonRect.width / 2)}px`,
    );
  };

  const syncNavOverflowState = () => {
    const headerRect = header.getBoundingClientRect();
    const navRect = nav.getBoundingClientRect();
    const hasOverflow =
      mediaList.matches && menu.scrollWidth - menu.clientWidth > 1;
    const isAtStart = menu.scrollLeft <= 1;
    const isAtEnd =
      menu.scrollLeft + menu.clientWidth >= menu.scrollWidth - 1;

    nav.style.setProperty(
      "--navimow-header-nav-inset-left",
      `${Math.max(0, Math.round(navRect.left - headerRect.left))}px`,
    );
    nav.style.setProperty(
      "--navimow-header-nav-inset-right",
      `${Math.max(0, Math.round(headerRect.right - navRect.right))}px`,
    );
    nav.setAttribute("data-nav-overflow", String(hasOverflow));
    nav.setAttribute("data-nav-scroll-start", String(!hasOverflow || isAtStart));
    nav.setAttribute("data-nav-scroll-end", String(!hasOverflow || isAtEnd));
    syncDesktopMenuPanelPosition();
  };

  const scheduleNavOverflowSync = () => {
    if (navOverflowFrame) return;

    navOverflowFrame = requestAnimationFrame(() => {
      navOverflowFrame = 0;
      syncNavOverflowState();
    });
  };

  navScrollButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const direction = button.dataset.direction;
      const left =
        direction === "previous" ? 0 : menu.scrollWidth - menu.clientWidth;

      menu.scrollTo({ left, behavior: "smooth" });
    });
  });

  let headerTop = 0;
  let headerTopFrame = 0;
  let scrollLockFrame = 0;
  let scrollLockFrameAfterPaint = 0;
  let closeCleanupTimeout = 0;
  let closeCleanupController = null;
  const syncMobileMenuOffset = () => {
    const headerContainer = header.parentElement;
    const top = headerContainer
      ? Math.max(0, Math.round(headerContainer.getBoundingClientRect().top))
      : 0;
    if (top === headerTop) return;

    headerTop = top;
    document.documentElement.style.setProperty("--header-top", `${top}px`);
  };

  const scheduleMobileMenuOffsetSync = () => {
    if (headerTopFrame) return;

    headerTopFrame = requestAnimationFrame(() => {
      headerTopFrame = 0;
      syncMobileMenuOffset();
    });
  };

  const clearScheduledScrollLock = () => {
    if (scrollLockFrame) {
      cancelAnimationFrame(scrollLockFrame);
      scrollLockFrame = 0;
    }

    if (scrollLockFrameAfterPaint) {
      cancelAnimationFrame(scrollLockFrameAfterPaint);
      scrollLockFrameAfterPaint = 0;
    }
  };

  const scheduleBodyScrollLock = () => {
    clearScheduledScrollLock();

    // Let the menu paint first, then lock body scroll outside the INP window.
    scrollLockFrame = requestAnimationFrame(() => {
      scrollLockFrame = 0;
      scrollLockFrameAfterPaint = requestAnimationFrame(() => {
        scrollLockFrameAfterPaint = 0;

        if (!isMobileMenuOpen()) return;
        document.body.classList.add("no-scroll");
      });
    });
  };

  const clearScheduledCloseCleanup = () => {
    if (closeCleanupTimeout) {
      clearTimeout(closeCleanupTimeout);
      closeCleanupTimeout = 0;
    }

    closeCleanupController?.abort();
    closeCleanupController = null;
  };

  const runCloseCleanup = () => {
    clearScheduledCloseCleanup();
    document.body.classList.remove("no-scroll");
    resetExpandableMenus();
  };

  const resetExpandableMenus = () => {
    nav
      .querySelectorAll(".header-menu-list, .header-menu-mega, .header-menu-multilist")
      .forEach((detail) => {
        detail.classList.remove("header-menu-open");
        detail.classList.add("header-menu-closed");
      });

    nav.querySelectorAll("button[aria-expanded]").forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });

    nav.querySelectorAll(".header-menu-mega").forEach((detail) => {
      detail.style.setProperty("--header-menu-mega-content-height", "0px");
    });

    nav.querySelectorAll(".header-menu-mega-item").forEach((item) => {
      item.style.removeProperty("--mega-item-content-height");
    });
  };

  const scheduleMobileMenuCloseCleanup = (deferCloseCleanup) => {
    clearScheduledCloseCleanup();

    if (!deferCloseCleanup) {
      runCloseCleanup();
      return;
    }

    closeCleanupController = new AbortController();
    const { signal } = closeCleanupController;
    const completeClose = (event) => {
      if (
        event &&
        (event.target !== mobileMenuInner || event.propertyName !== "opacity")
      ) {
        return;
      }

      runCloseCleanup();
    };

    mobileMenuInner.addEventListener("transitionend", completeClose, {
      signal,
    });
    closeCleanupTimeout = window.setTimeout(
      completeClose,
      MOBILE_MENU_TRANSITION_MS + 80,
    );
  };

  const setMobileMenuOpen = (
    open,
    { deferCloseCleanup = !mediaList.matches } = {},
  ) => {
    header.setAttribute("data-mobile-menu-open", String(open));
    mobileMenuButton.setAttribute("aria-expanded", String(open));

    if (open) {
      clearScheduledCloseCleanup();
      scheduleBodyScrollLock();
    } else {
      clearScheduledScrollLock();
      scheduleMobileMenuCloseCleanup(deferCloseCleanup);
    }

    updateNavAccessibility();
  };

  const clearNavigationPending = () => {
    header.removeAttribute("data-navigation-pending");
  };

  nav.addEventListener("click", (event) => {
    if (!mediaList.matches || !(event.target instanceof Element)) return;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const link = event.target.closest("a[href]");
    if (!link || !nav.contains(link) || link.hasAttribute("download")) return;

    const linkTarget = link.getAttribute("target")?.toLowerCase();
    if (linkTarget && linkTarget !== "_self") return;

    let destination;
    try {
      destination = new URL(link.href, window.location.href);
    } catch {
      return;
    }

    if (!["http:", "https:"].includes(destination.protocol)) return;

    const isSameDocumentHash =
      destination.origin === window.location.origin &&
      destination.pathname === window.location.pathname &&
      destination.search === window.location.search &&
      destination.hash;
    if (isSameDocumentHash) return;

    header.setAttribute("data-navigation-pending", "true");
    queueMicrotask(() => {
      if (event.defaultPrevented) clearNavigationPending();
    });
  });

  window.addEventListener("pageshow", clearNavigationPending);

  nav.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    if (!mediaList.matches) {
      setMobileMenuOpen(false);
      mobileMenuButton.focus();
      return;
    }

    resetExpandableMenus();
    updateNavAccessibility();
  });

  mobileMenuButton.addEventListener("click", () => {
    scheduleMobileMenuOffsetSync();
    setMobileMenuOpen(!isMobileMenuOpen());
  });

  addMediaChangeListener(mediaList, () => {
    scheduleMobileMenuOffsetSync();
    scheduleNavOverflowSync();
    setMobileMenuOpen(false, { deferCloseCleanup: false });
  });

  window.addEventListener(
    "resize",
    () => {
      scheduleMobileMenuOffsetSync();
      scheduleNavOverflowSync();
    },
    { passive: true },
  );

  menu.addEventListener("scroll", scheduleNavOverflowSync, { passive: true });

  if ("ResizeObserver" in window) {
    const navResizeObserver = new ResizeObserver(scheduleNavOverflowSync);
    navResizeObserver.observe(header);
    navResizeObserver.observe(nav);
    navResizeObserver.observe(menu);
  }

  document.fonts?.ready?.then?.(scheduleNavOverflowSync);

  scheduleMobileMenuOffsetSync();
  scheduleNavOverflowSync();
  updateNavAccessibility();

  const bindResponsiveMode = (bind) => {
    let cleanup = () => {};

    const apply = () => {
      cleanup();
      cleanup = bind(mediaList.matches) || (() => {});
    };

    apply();
    addMediaChangeListener(mediaList, apply);
  };

  /**
   * list menu
   */
  const listDetails = nav.querySelectorAll(".header-menu-list");

  listDetails.forEach((detail) => {
    const button = detail.querySelector(".header-menu-list-button");
    const detailContent = detail.querySelector(
      ".header-menu-list-content_list",
    );
    if (!button || !detailContent?.firstElementChild) return;

    const measureContentHeight = () => {
      detail.style.setProperty(
        "--header-menu-list-content-height",
        `${detailContent.firstElementChild.scrollHeight}px`,
      );
    };

    measureContentHeight();

    const transitionReset = () => {
      detail.classList.add("header-menu-closed");
      button.setAttribute("aria-expanded", "false");
    };

    detailContent.addEventListener("transitionend", (event) => {
      if (event.target !== detailContent) return;
      if (detail.classList.contains("header-menu-open")) return;
      transitionReset();
    });

    let mouseOverTimeout = null;

    const triggerHeightTransition = (open) => {
      if (open) {
        syncDesktopMenuPanelPosition(button);
        measureContentHeight();
        detail.classList.add("header-menu-open");
        detail.classList.remove("header-menu-closed");
        button.setAttribute("aria-expanded", "true");
      } else {
        detail.classList.remove("header-menu-open");
        if (detailContent.clientHeight === 0) {
          transitionReset();
        }
      }
    };

    const mouseEnterHandler = () => {
      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = setTimeout(() => {
        triggerHeightTransition(true);
      }, 120);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const mouseLeaveHandler = () => {
      clearTimeout(mouseOverTimeout);
      triggerHeightTransition(false);
    };

    const openFromButtonInteraction = () => {
      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = null;
      triggerHeightTransition(true);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const closeWhenDocumentIsHidden = () => {
      if (!document.hidden) return;

      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = null;
      detail.removeEventListener("mouseleave", mouseLeaveHandler);
      triggerHeightTransition(false);
      transitionReset();
    };

    bindResponsiveMode((isDesktop) => {
      clearTimeout(mouseOverTimeout);
      const controller = new AbortController();
      const { signal } = controller;

      if (isDesktop) {
        detail.addEventListener("mouseenter", mouseEnterHandler, { signal });
        document.addEventListener(
          "visibilitychange",
          closeWhenDocumentIsHidden,
          { signal },
        );
        button.addEventListener("click", openFromButtonInteraction, { signal });
        button.addEventListener(
          "focus",
          () => {
            if (!button.matches(":focus-visible")) return;
            openFromButtonInteraction();
          },
          { signal },
        );
      } else {
        button.addEventListener(
          "click",
          () => {
            triggerHeightTransition(!detail.classList.contains("header-menu-open"));
          },
          { signal },
        );
      }

      return () => {
        clearTimeout(mouseOverTimeout);
        detail.removeEventListener("mouseleave", mouseLeaveHandler);
        controller.abort();
      };
    });
  });

  /**
   * mega menu
   */

  const megaDetails = nav.querySelectorAll(".header-menu-mega");
  const MEGA_MENU_OPEN_DELAY = 140;
  const MEGA_MENU_SWITCH_DELAY = 260;
  const MEGA_MENU_CLOSE_DELAY = 160;
  const MEGA_ITEM_HOVER_DELAY = 180;
  const MEGA_ITEM_INTENT_DELAY = 320;
  const MOUSE_LOCATIONS_TRACKED = 3;

  megaDetails.forEach((detail) => {
    const button = detail.querySelector(".header-menu-mega-button");
    const detailContent = detail.querySelector(
      ".header-menu-mega-content_list",
    );
    if (!button || !detailContent?.firstElementChild) return;

    detail.style.setProperty("--header-menu-mega-content-height", `${0}px`);

    const transitionReset = () => {
      detail.classList.add("header-menu-closed");
      button.setAttribute("aria-expanded", "false");
    };

    detailContent.addEventListener("transitionend", (event) => {
      if (event.target !== detailContent) return;
      if (detail.classList.contains("header-menu-open")) return;
      transitionReset();
    });

    let mouseOverTimeout = null;
    let mouseLeaveTimeout = null;
    let megaItemHoverTimeout = null;
    let activeMegaItemIdx = 0;
    const mouseLocations = [];

    const triggerHeightTransition = (open) => {
      if (open) {
        syncDesktopMenuPanelPosition(button);
        detail.classList.add("header-menu-open");
        detail.classList.remove("header-menu-closed");
        button.setAttribute("aria-expanded", "true");
      } else {
        detail.classList.remove("header-menu-open");
        if (detailContent.clientHeight === 0) {
          transitionReset();
        }
      }
    };

    const clearMegaItemHoverTimeout = () => {
      clearTimeout(megaItemHoverTimeout);
      megaItemHoverTimeout = null;
    };

    const resetMouseLocations = () => {
      mouseLocations.length = 0;
    };

    const trackMouseLocation = (event) => {
      mouseLocations.push({ x: event.clientX, y: event.clientY });
      if (mouseLocations.length > MOUSE_LOCATIONS_TRACKED) {
        mouseLocations.shift();
      }
    };

    const getOtherOpenedMega = () =>
      Array.from(megaDetails).find(
        (item) =>
          item !== detail && item.classList.contains("header-menu-open"),
      );

    const mouseEnterHandler = () => {
      clearTimeout(mouseLeaveTimeout);
      clearTimeout(mouseOverTimeout);
      const delay = getOtherOpenedMega()
        ? MEGA_MENU_SWITCH_DELAY
        : MEGA_MENU_OPEN_DELAY;
      mouseOverTimeout = setTimeout(() => {
        triggerHeightTransition(true);
        mouseOverTimeout = null;
      }, delay);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const mouseLeaveHandler = () => {
      clearTimeout(mouseOverTimeout);
      clearMegaItemHoverTimeout();
      clearTimeout(mouseLeaveTimeout);
      mouseLeaveTimeout = setTimeout(() => {
        triggerHeightTransition(false);
        resetMouseLocations();
        mouseLeaveTimeout = null;
      }, MEGA_MENU_CLOSE_DELAY);
    };

    const megaItemButton = Array.from(
      detail.querySelectorAll(".header-menu-mega-item-button"),
    );
    const resetMegaItemState = () => {
      megaItemButton.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
        item.parentElement?.style.removeProperty("--mega-item-content-height");
      });
    };

    megaItemButton.forEach((item, index) => {
      item.dataset.idx = index;
    });

    const openMegaItemDetail = (idx) => {
      const currentButton = megaItemButton[idx];
      if (!currentButton) return;
      activeMegaItemIdx = idx;
      megaItemButton.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });
      currentButton.setAttribute("aria-expanded", "true");

      const currentContent = currentButton.nextElementSibling;
      detail.style.setProperty(
        "--mega-item-content-height",
        `${currentContent.scrollHeight}px`,
      );
      detail.style.setProperty(
        "--header-menu-mega-content-height",
        `${detailContent.firstElementChild.clientHeight}px`,
      );

      // 防止 swiper 的 bug
      const swiperNodes =
        currentButton.parentElement.querySelectorAll(".swiper");
      swiperNodes.forEach((node) => {
        const swiper = node.swiper;
        if (swiper) swiper.scrollbar.enable();
      });
    };

    const getMegaItemHoverDelay = (nextIdx) => {
      if (nextIdx === activeMegaItemIdx) return 0;

      const currentButton = megaItemButton[activeMegaItemIdx];
      const currentContent = currentButton?.nextElementSibling;
      const firstLocation = mouseLocations[0];
      const latestLocation = mouseLocations[mouseLocations.length - 1];

      if (!currentContent || !firstLocation || !latestLocation) {
        return MEGA_ITEM_HOVER_DELAY;
      }

      const currentContentRect = currentContent.getBoundingClientRect();
      const isMovingTowardContent =
        latestLocation.x > firstLocation.x &&
        latestLocation.x <= currentContentRect.left + 48 &&
        latestLocation.y >= currentContentRect.top - 80 &&
        latestLocation.y <= currentContentRect.bottom + 80 &&
        firstLocation.x < currentContentRect.left &&
        firstLocation.y >= currentContentRect.top - 120 &&
        firstLocation.y <= currentContentRect.bottom + 120;

      return isMovingTowardContent
        ? MEGA_ITEM_INTENT_DELAY
        : MEGA_ITEM_HOVER_DELAY;
    };

    const scheduleMegaItemDetail = (idx) => {
      clearMegaItemHoverTimeout();

      const delay = getMegaItemHoverDelay(idx);
      if (delay === 0) return;

      megaItemHoverTimeout = setTimeout(() => {
        openMegaItemDetail(idx);
        megaItemHoverTimeout = null;
      }, delay);
    };

    const openFromButtonInteraction = () => {
      clearTimeout(mouseLeaveTimeout);
      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = null;
      triggerHeightTransition(true);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const closeWhenDocumentIsHidden = () => {
      if (!document.hidden) return;

      clearTimeout(mouseOverTimeout);
      clearTimeout(mouseLeaveTimeout);
      mouseOverTimeout = null;
      mouseLeaveTimeout = null;
      clearMegaItemHoverTimeout();
      resetMouseLocations();
      detail.removeEventListener("mouseleave", mouseLeaveHandler);
      triggerHeightTransition(false);
      transitionReset();
    };

    bindResponsiveMode((isDesktop) => {
      clearTimeout(mouseOverTimeout);
      clearTimeout(mouseLeaveTimeout);
      clearMegaItemHoverTimeout();
      resetMouseLocations();

      const controller = new AbortController();
      const { signal } = controller;

      if (isDesktop) {
        detail.addEventListener("mousemove", trackMouseLocation, { signal });
        detail.addEventListener("mouseenter", mouseEnterHandler, { signal });
        document.addEventListener(
          "visibilitychange",
          closeWhenDocumentIsHidden,
          { signal },
        );
        button.addEventListener("click", openFromButtonInteraction, { signal });
        button.addEventListener(
          "focus",
          () => {
            if (!button.matches(":focus-visible")) return;
            openFromButtonInteraction();
          },
          { signal },
        );

        megaItemButton.forEach((item) => {
          item.addEventListener(
            "mouseenter",
            () => {
              const idx = Number(item.dataset.idx);
              scheduleMegaItemDetail(idx);
            },
            { signal },
          );
          item.addEventListener(
            "mouseleave",
            () => {
              clearMegaItemHoverTimeout();
            },
            { signal },
          );
          item.addEventListener(
            "focus",
            () => {
              clearMegaItemHoverTimeout();
              const idx = Number(item.dataset.idx);
              openMegaItemDetail(idx);
            },
            { signal },
          );
        });

        openMegaItemDetail(0);
      } else {
        button.addEventListener(
          "click",
          () => {
            const willOpen = !detail.classList.contains("header-menu-open");

            detail.style.setProperty(
              "--header-menu-mega-content-height",
              `${detailContent.firstElementChild.clientHeight}px`,
            );

            if (!willOpen) {
              resetMegaItemState();
            }

            triggerHeightTransition(willOpen);
          },
          { signal },
        );

        megaItemButton.forEach((item) => {
          item.addEventListener(
            "click",
            () => {
              const isExpanded = item.getAttribute("aria-expanded") === "true";
              const currentContent = item.nextElementSibling?.querySelector(
                ".mega-item-content_inner",
              );
              if (!currentContent) return;

              item.parentElement?.style.setProperty(
                "--mega-item-content-height",
                `${currentContent.scrollHeight}px`,
              );
              item.setAttribute("aria-expanded", isExpanded ? "false" : "true");

              if (!isExpanded) {
                detail.style.setProperty(
                  "--header-menu-mega-content-height",
                  "auto",
                );
              }
            },
            { signal },
          );

          item.nextElementSibling?.addEventListener(
            "transitionend",
            (event) => {
              if (event.target !== item.nextElementSibling) return;
              const height = detailContent.firstElementChild.clientHeight;
              detail.style.setProperty(
                "--header-menu-mega-content-height",
                `${height}px`,
              );
            },
            { signal },
          );
        });
      }

      return () => {
        clearTimeout(mouseOverTimeout);
        clearTimeout(mouseLeaveTimeout);
        clearMegaItemHoverTimeout();
        resetMouseLocations();
        detail.removeEventListener("mouseleave", mouseLeaveHandler);
        controller.abort();
      };
    });
  });

  /**
   * multilist menu
   */

  const multilistDetails = nav.querySelectorAll(".header-menu-multilist");

  multilistDetails.forEach((detail) => {
    const button = detail.querySelector(".header-menu-multilist-button");
    const detailContent = detail.querySelector(
      ".header-menu-multilist-content_list",
    );
    if (!button || !detailContent?.firstElementChild) return;

    const measureContentHeight = () => {
      detail.style.setProperty(
        "--header-menu-multilist-content-height",
        `${detailContent.firstElementChild.scrollHeight}px`,
      );
    };

    measureContentHeight();

    const transitionReset = () => {
      detail.classList.add("header-menu-closed");
      button.setAttribute("aria-expanded", "false");
    };

    detailContent.addEventListener("transitionend", (event) => {
      if (event.target !== detailContent) return;
      if (detail.classList.contains("header-menu-open")) return;
      transitionReset();
    });

    let mouseOverTimeout = null;

    const triggerHeightTransition = (open) => {
      if (open) {
        syncDesktopMenuPanelPosition(button);
        measureContentHeight();
        detail.classList.add("header-menu-open");
        detail.classList.remove("header-menu-closed");
        button.setAttribute("aria-expanded", "true");
      } else {
        detail.classList.remove("header-menu-open");
        if (detailContent.clientHeight === 0) {
          transitionReset();
        }
      }
    };

    const mouseEnterHandler = () => {
      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = setTimeout(() => {
        triggerHeightTransition(true);
      }, 120);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const mouseLeaveHandler = () => {
      clearTimeout(mouseOverTimeout);
      triggerHeightTransition(false);
    };

    const openFromButtonInteraction = () => {
      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = null;
      triggerHeightTransition(true);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const closeWhenDocumentIsHidden = () => {
      if (!document.hidden) return;

      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = null;
      detail.removeEventListener("mouseleave", mouseLeaveHandler);
      triggerHeightTransition(false);
      transitionReset();
    };

    bindResponsiveMode((isDesktop) => {
      clearTimeout(mouseOverTimeout);
      const controller = new AbortController();
      const { signal } = controller;

      if (isDesktop) {
        detail.addEventListener("mouseenter", mouseEnterHandler, { signal });
        document.addEventListener(
          "visibilitychange",
          closeWhenDocumentIsHidden,
          { signal },
        );
        button.addEventListener("click", openFromButtonInteraction, { signal });
        button.addEventListener(
          "focus",
          () => {
            if (!button.matches(":focus-visible")) return;
            openFromButtonInteraction();
          },
          { signal },
        );
      } else {
        button.addEventListener(
          "click",
          () => {
            triggerHeightTransition(!detail.classList.contains("header-menu-open"));
          },
          { signal },
        );
      }

      return () => {
        clearTimeout(mouseOverTimeout);
        detail.removeEventListener("mouseleave", mouseLeaveHandler);
        controller.abort();
      };
    });
  });

  // track event

  const trackEvent = (data) => {
    window.dataLayer = window.dataLayer || [];
    const current_page = window.location.href;
    window.dataLayer.push({
      event: "ga4Event",
      event_name: "header-cta",
      event_parameters: {
        current_page: current_page,
        ...data,
      },
    });
  };

  const headerMenuMegaLink = header.querySelectorAll(".header-menu-mega-link-item");
  headerMenuMegaLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          .querySelector(".header-menu-mega-button").innerText,
        button_name: link.querySelector(".header-menu-mega-link-text")
          ?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuMegaItemBannerLink = header.querySelectorAll(
    ".header-menu-mega-item-banner-link",
  );
  headerMenuMegaItemBannerLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          ?.querySelector(".header-menu-mega-button")?.innerText,
        subheading: link
          .closest(".header-menu-mega-item")
          ?.querySelector(".header-menu-mega-item-button-text")?.innerText,
        title: link?.querySelector(".header-menu-mega-item-banner-link-title")
          ?.innerText,
        button_name: "banner_image",
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuMegaItemViewAllLink = header.querySelectorAll(
    ".header-menu-mega-item-view-all-link",
  );
  headerMenuMegaItemViewAllLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          ?.querySelector(".header-menu-mega-button")?.innerText,
        subheading: link
          .closest(".header-menu-mega-item")
          ?.querySelector(".header-menu-mega-item-button-text")?.innerText,
        button_name: link.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuMegaItemProductLink = header.querySelectorAll(
    ".header-menu-mega-item-product-link",
  );
  headerMenuMegaItemProductLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          ?.querySelector(".header-menu-mega-button")?.innerText,
        subheading: link
          .closest(".header-menu-mega-item")
          ?.querySelector(".header-menu-mega-item-button-text")?.innerText,
        title: link?.querySelector(".header-menu-mega-item-product-title")
          ?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuListItemLink = header.querySelectorAll(
    ".header-menu-list-item-link",
  );
  headerMenuListItemLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-multilist")
          ?.querySelector(".header-menu-multilist-button")?.innerText,
        subheading: link
          .closest(".header-menu-list-item")
          ?.querySelector(".header-menu-list-item-title")?.innerText,
        button_name: link?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuListLink = header.querySelectorAll(".header-menu-list-link");
  headerMenuListLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-list")
          ?.querySelector(".header-menu-list-button")?.innerText,
        button_name: link?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuLink = header.querySelectorAll(".header-menu-link");
  headerMenuLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: "header menu link",
        button_name: link?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

})();
